import { AnchorMode, CenteringStatus } from '@utils/anchors'
import { FileReference as FileReference } from './FileReference'
import { ModelPermission } from './ModelPermission'

export type ModelOptions = {
    id?: number,
    description?: string,
    position?: [number, number, number],
    rotation?: [number, number, number],
    scale?: [number, number, number],
    thumbnail?: string,
    color?: string,
    overrideColor?: boolean,
    opacity?: number,
    lastChanged?: string,
    sharedWithOrganization?: boolean,
    s3Files?: ModelFile[],
    organizationID?: string,
    permissions?: ModelPermission[],
    state?: string
    wgs84_position?: [number, number, number]
    vertical_crs?: string
    horizontal_crs?: string
    bounding_box_min?: [number, number, number]
    bounding_box_max?: [number, number, number]
    type?: 'mesh' | 'point-cloud' | 'splat'
    anchor_mode?: AnchorMode
    custom_anchor?: [number, number, number, number, number] | null
    anchor_centering_status?: CenteringStatus | null
    anchor_centering_origin_wgs84?: [number, number, number] | null
    anchor_bbox_min_wgs84?: [number, number, number] | null
    centering_origin?: [number, number, number] | null
}

export class Model {
    public id: number
    public name: string
    public description: string
    public position: [number, number, number]
    public rotation: [number, number, number]
    public scale: [number, number, number]
    public color: string
    public overrideColor: boolean
    public opacity: number
    public lastChanged: string
    public files: FileReference[]
    public s3Files: ModelFile[]
    public sharedWithOrganization: boolean
    public organizationID: string
    public permissions: ModelPermission[]
    public updateThumbnail: boolean = false
    public state: string
    public wgs84_position: [number, number, number]
    public vertical_crs: string
    public horizontal_crs: string
    public bounding_box_min: [number, number, number]
    public bounding_box_max: [number, number, number]
    public type: 'mesh' | 'point-cloud' | 'splat'
    // Point-cloud anchor placement. The Potree octree is always centered; the
    // viewer reads anchor_mode + centering_origin to position the group in
    // local Three.js space — centered → [0,0,0], default → centering_origin,
    // custom → custom_anchor[0..2]. The anchor_*_wgs84 columns are retained for
    // Map-scene WGS84 placement (drives the 'crs' mode), separate from the
    // non-geo modes.
    public anchor_mode: AnchorMode
    public custom_anchor: [number, number, number, number, number] | null
    public anchor_centering_status: CenteringStatus | null
    public anchor_centering_origin_wgs84: [number, number, number] | null
    public anchor_bbox_min_wgs84: [number, number, number] | null
    public centering_origin: [number, number, number] | null

    /** Raw s3ref: values must never be used as img src — they cause ERR_UNKNOWN_URL_SCHEME.
     *  The getter/setter pair ensures any unresolved s3ref: value is treated as empty. */
    private _thumbnail: string = ""
    get thumbnail(): string { return this._thumbnail }
    set thumbnail(value: string) {
        if (value?.startsWith('s3ref:')) {
            console.warn(`[Model] Blocked raw s3ref thumbnail for model ${this.id ?? '(new)'}: ${value}`)
            this._thumbnail = ""
        } else {
            this._thumbnail = value ?? ""
        }
    }

    constructor(name: string, files: FileReference[], options: Partial<Model> = {}) {
        this.id = options.id ?? null
        this.name = name
        this.description = options.description ?? ""
        this.position = options.position ?? [0, 0, 0]
        this.rotation = options.rotation ?? [0, 0, 0]
        this.scale = options.scale ?? [1, 1, 1]
        this.thumbnail = options.thumbnail ?? ""
        this.color = options.color ?? '#000000'
        this.overrideColor = options.overrideColor ?? false
        this.opacity = options.opacity ?? 1.0
        this.lastChanged = options.lastChanged ?? ""
        this.files = files ? [...files] : []
        this.s3Files = options.s3Files ? [...options.s3Files] : []
        this.sharedWithOrganization = options.sharedWithOrganization ?? false
        this.organizationID = options.organizationID
        this.permissions = options.permissions ? [...options.permissions] : []
        this.state = options.state
        this.wgs84_position = options.wgs84_position
        this.vertical_crs = options.vertical_crs
        this.horizontal_crs = options.horizontal_crs
        this.bounding_box_min = options.bounding_box_min
        this.bounding_box_max = options.bounding_box_max
        this.type = options.type ?? 'mesh'
        this.anchor_mode = options.anchor_mode ?? 'default'
        this.custom_anchor = options.custom_anchor ?? null
        this.anchor_centering_status = options.anchor_centering_status ?? null
        this.anchor_centering_origin_wgs84 = options.anchor_centering_origin_wgs84 ?? null
        this.anchor_bbox_min_wgs84 = options.anchor_bbox_min_wgs84 ?? null
        this.centering_origin = options.centering_origin ?? null
    }
}

export function equal(model1: Model, model2: Model): boolean {
    return physicallyEqual(model1, model2)
        && filesEqual(model1, model2)
        && model1.name == model2.name
        && model1.description == model2.description
        && model1.thumbnail == model2.thumbnail
        && model1.sharedWithOrganization == model2.sharedWithOrganization
        && model1.organizationID == model2.organizationID
        && model1.permissions.length == model2.permissions.length
        && model1.permissions.every(perm => model2.permissions.some(p => perm.equals(p)))
}

export function physicallyEqual(model1: Model, model2: Model): boolean {
    return model1.id == model2.id
        && model1.position.toString() == model2.position.toString()
        && model1.rotation.toString() == model2.rotation.toString()
        && model1.scale.toString() == model2.scale.toString()
        && model1.color == model2.color
        && model1.overrideColor == model2.overrideColor
        && model1.opacity == model2.opacity
}

export function filesEqual(model1: Model, model2: Model): boolean {
    const traditionalFilesEqual = model1.files.length == model2.files.length
        && model1.files.every(file => model2.files.find(f => f.hash == file.hash))

    const s3Files1 = model1.s3Files ?? []
    const s3Files2 = model2.s3Files ?? []
    const s3FilesEqual = s3Files1.length == s3Files2.length
        && s3Files1.every(s3File => s3Files2.find(f => f.key == s3File.key && f.bucket == s3File.bucket))

    return traditionalFilesEqual && s3FilesEqual
}

export interface ModelFile {
    modelID: number
    s3ObjectReferenceId: number
    bucket: string
    key: string
    label: string
    type: "file" | "folder"
    size?: number | null
}

export interface ModelFileResult {
    model_id: string
    s3_object_reference_id: string
    bucket: string
    key: string
    label: string
    type: "file" | "folder"
    size?: string | null
}